import { Clock, FileText } from "lucide-react";
import { useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import type { InboxAttentionState, InboxPriority } from "../../types";
import {
  attentionToneClass,
  freeformWindowStatus,
  normalizePriority,
} from "./inboxModel";

const PRIORITY_DOT: Record<InboxPriority, string> = {
  high: "bg-red-500",
  normal: "bg-amber-500",
  low: "bg-slate-400",
};

export const PriorityDot = ({
  priority,
  className,
}: {
  priority: string | null | undefined;
  className?: string;
}) => {
  const translate = useTranslate();
  const key = normalizePriority(priority);
  const label = translate(`crm.whatsapp.inbox.priority.${key}`);
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        "inline-block size-2.5 shrink-0 rounded-full",
        PRIORITY_DOT[key],
        className,
      )}
    />
  );
};

export const ReasonChip = ({
  label,
  className,
}: {
  label: string | null | undefined;
  className?: string;
}) => {
  const translate = useTranslate();
  return (
    <Badge
      variant="outline"
      className={cn("max-w-full truncate font-normal", className)}
    >
      {label || translate("crm.whatsapp.inbox.reason_unknown")}
    </Badge>
  );
};

/** 24h freeform window countdown, or "Template needed" once it has closed. */
export const WindowBadge = ({
  expiresAt,
  now,
}: {
  expiresAt: string | null | undefined;
  now: number;
}) => {
  const translate = useTranslate();
  const status = freeformWindowStatus(expiresAt, now);
  if (status.kind === "open") {
    return (
      <Badge variant="secondary" className="gap-1 font-normal tabular-nums">
        <Clock className="size-3" />
        {translate("crm.whatsapp.inbox.window_open", { time: status.label })}
      </Badge>
    );
  }
  if (status.kind === "closed") {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-300 font-normal text-amber-800 dark:border-amber-800 dark:text-amber-300"
      >
        <FileText className="size-3" />
        {translate("crm.whatsapp.inbox.window_closed")}
      </Badge>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {translate("crm.whatsapp.inbox.window_unknown")}
    </span>
  );
};

export const InboxStateBadge = ({ state }: { state: InboxAttentionState }) => {
  const translate = useTranslate();
  if (state === "none") return null;
  return (
    <Badge
      variant="outline"
      className={cn("font-normal", attentionToneClass(state))}
    >
      {translate(`crm.whatsapp.inbox.tabs.${state}`)}
    </Badge>
  );
};
